import type { FormState } from 'typings'
import {
  Grid,
  ParentRelationshipGrid,
  CheckboxContainer,
  QuestionText
} from 'ui/molecules/RequestPage.styles'
import { ComponentInterface } from 'typings'
import Icon from 'ui/atoms/Icon'
import { OptionType } from 'syf-component-library/ui/atoms/Select/typings'
import {
  CheckboxContainerPadding,
  Message,
  StyledCheckbox,
  StyledH2,
  StyledSelect,
  StyledSelectContainer,
  StyledTextarea,
  StyledTextfield
} from 'pages/InfoRequestPage.styles'
import { getCleanState } from 'helpers'
import useCustomTranslation from 'hooks/useCustomTranslation'
import {
  showAdditionalRelationshipQuestion,
  showEmployeeQuestion
} from 'helpers/relationshipQuestions'

const Relationship = ({
  formValues,
  handleOnChange,
  handleOnBlur,
  handleState
}: ComponentInterface): JSX.Element => {
  const t = useCustomTranslation('form')

  const cleanBusinessCommercialAccount = (
    unsureRelationship: boolean
  ): Partial<FormState> => {
    return {
      unsureRelationship,
      creditCardChecked: false,
      creditCard: '',
      savingAccountChecked: false,
      savingProduct: '',
      installmentAccountChecked: false,
      installmentAccount: '',
      businessCommercialAccountChecked: false,
      businessCommercialAccount: '',
      primaryAccountPayable: { label: '', value: '' },
      errors: {
        ...formValues.errors,
        businessCommercialAccount: ''
      }
    }
  }

  const noRelationshipWithSynchrony =
    formValues.relationshipSynchrony.value === 'No' ||
    formValues.relationshipSynchrony.value === ''

  return (
    <ParentRelationshipGrid style={{ marginTop: '1.5rem' }}>
      <StyledH2 style={{ maxWidth: '500px', width: '80vw' }}>
        {t('relationship.title')}
      </StyledH2>
      <QuestionText style={{ marginTop: '1.5rem' }}>
        {t('relationship.account')}
      </QuestionText>
      <CheckboxContainer style={{ marginBottom: '2rem' }}>
        <StyledSelect
          id="relationshipSynchrony"
          name="relationshipSynchrony"
          placeholder={t('optionPlaceholder') || ''}
          options={[
            { label: t('yes'), value: 'Yes' },
            { label: t('no'), value: 'No' }
          ]}
          errorIcon={Icon('error')}
          error={formValues.errors.relationshipSynchrony}
          defaultOption={formValues.relationshipSynchrony}
          onChange={(value: OptionType) => {
            const unsureRelationship =
              formValues.employee?.value === 'No' && value.value === 'No'
            const additionalState =
              value?.value === 'No'
                ? cleanBusinessCommercialAccount(unsureRelationship)
                : { unsureRelationship }
            handleOnChange('relationshipSynchrony', value, additionalState)
          }}
          rightIconOpen={Icon('up')}
          rightIconClosed={Icon('down')}
          data-testid="relationship-synchrony"
        />
      </CheckboxContainer>
      <CheckboxContainer>
        <StyledCheckbox
          id="credit"
          isDisabled={noRelationshipWithSynchrony}
          isChecked={formValues.creditCardChecked}
          onChange={() => {
            const { checked, state } = getCleanState(
              'creditCardChecked',
              formValues
            )
            handleState({
              ...state,
              creditCardChecked: checked,
              unsureRelationship: false
            })
          }}
          data-testid="is-credit"
          className="margin-top"
        >
          {t('relationship.credit')}
        </StyledCheckbox>
        <StyledTextfield
          type="text"
          id="creditCard"
          name="creditCard"
          placeholder={t('relationship.credit')}
          matchPattern={/^\d*$/}
          error={formValues.errors.creditCard}
          value={formValues.creditCard}
          isDisabled={!formValues.creditCardChecked}
          onChange={e =>
            handleOnChange('creditCard', (e.target as HTMLInputElement).value)
          }
          maxLength={18}
          errorIcon={Icon('error')}
          data-testid="credit-card"
        />
      </CheckboxContainer>
      <CheckboxContainer>
        <StyledCheckbox
          id="saving"
          isDisabled={noRelationshipWithSynchrony}
          isChecked={formValues.savingAccountChecked}
          onChange={() => {
            const { checked, state } = getCleanState(
              'savingAccountChecked',
              formValues
            )
            handleState({
              ...state,
              savingAccountChecked: checked,
              unsureRelationship: false
            })
          }}
          data-testid="is-saving"
          className="margin-top"
        >
          {t('relationship.saving')}
        </StyledCheckbox>
        <StyledTextfield
          type="text"
          id="savingProduct"
          name="savingProduct"
          placeholder={t('relationship.saving')}
          matchPattern={/^\d*$/}
          error={formValues.errors.savingProduct}
          value={formValues.savingProduct}
          isDisabled={!formValues.savingAccountChecked}
          onChange={e =>
            handleOnChange(
              'savingProduct',
              (e.target as HTMLInputElement).value
            )
          }
          maxLength={18}
          errorIcon={Icon('error')}
          data-testid="saving-product"
        />
      </CheckboxContainer>
      <CheckboxContainer>
        <StyledCheckbox
          id="installment"
          isDisabled={noRelationshipWithSynchrony}
          isChecked={formValues.installmentAccountChecked}
          onChange={() => {
            const { checked, state } = getCleanState(
              'installmentAccountChecked',
              formValues
            )
            handleState({
              ...state,
              installmentAccountChecked: checked,
              unsureRelationship: false
            })
          }}
          data-testid="is-installment-accountCheck"
          className="margin-top"
        >
          {t('relationship.installment')}
        </StyledCheckbox>
        <StyledTextfield
          type="text"
          id="installmentAccount"
          name="installmentAccount"
          placeholder={t('relationship.installment')}
          matchPattern={/^\d*$/}
          error={formValues.errors.installmentAccount}
          value={formValues.installmentAccount}
          isDisabled={!formValues.installmentAccountChecked}
          onChange={e =>
            handleOnChange(
              'installmentAccount',
              (e.target as HTMLInputElement).value
            )
          }
          maxLength={18}
          errorIcon={Icon('error')}
          data-testid="installment-account"
        />
      </CheckboxContainer>
      <CheckboxContainer>
        <StyledCheckbox
          id="businessCommercialAccountChecked"
          isDisabled={noRelationshipWithSynchrony}
          isChecked={formValues.businessCommercialAccountChecked}
          onChange={() => {
            const { checked, state } = getCleanState(
              'businessCommercialAccountChecked',
              formValues
            )
            handleState({
              ...state,
              businessCommercialAccountChecked: checked,
              unsureRelationship: false
            })
          }}
          data-testid="is-business-commercial-account"
        >
          {t('relationship.business')}
        </StyledCheckbox>
      </CheckboxContainer>
      <QuestionText>{t('relationship.businessPrimary')}</QuestionText>
      <Grid>
        <StyledSelectContainer>
          <StyledSelect
            key={`primaryAccountPayable-${formValues.businessCommercialAccountChecked}`}
            id="primaryAccountPayable"
            name="primaryAccountPayable"
            placeholder={t('optionPlaceholder') || ''}
            options={[
              { label: t('yes'), value: 'Yes' },
              { label: t('no'), value: 'No' }
            ]}
            isDisabled={
              noRelationshipWithSynchrony ||
              !formValues.businessCommercialAccountChecked
            }
            errorIcon={Icon('error')}
            defaultOption={formValues.primaryAccountPayable}
            onChange={value => {
              const noPrimaryAccount = value.value === 'No'
              handleState({
                ...formValues,
                primaryAccountPayable: value,
                businessCommercialAccount: noPrimaryAccount
                  ? ''
                  : formValues.businessCommercialAccount,
                errors: {
                  ...formValues.errors,
                  businessCommercialAccount: noPrimaryAccount
                    ? ''
                    : formValues.errors.businessCommercialAccount
                }
              })
            }}
            rightIconOpen={Icon('up')}
            rightIconClosed={Icon('down')}
            data-testid="primary-account-payable"
          />
        </StyledSelectContainer>
        <StyledTextfield
          type="text"
          id="businessCommercialAccount"
          name="businessCommercialAccount"
          placeholder={t('relationship.business')}
          matchPattern={/^\d*$/}
          error={formValues.errors.businessCommercialAccount}
          value={formValues.businessCommercialAccount}
          isDisabled={
            noRelationshipWithSynchrony ||
            formValues.primaryAccountPayable.value === '' ||
            formValues.primaryAccountPayable.value === 'No'
          }
          onBlur={() =>
            formValues.primaryAccountPayable.value === 'Yes' &&
            !formValues.businessCommercialAccount &&
            handleOnBlur('businessCommercialAccount')
          }
          onChange={e =>
            handleOnChange(
              'businessCommercialAccount',
              (e.target as HTMLInputElement).value
            )
          }
          maxLength={18}
          errorIcon={Icon('error')}
          data-testid="business-commercial-account"
        />
        {formValues.primaryAccountPayable.value === 'No' && (
          <Message>{t('relationship.noPrimary')}</Message>
        )}
      </Grid>
      {showEmployeeQuestion(formValues.state.value) && (
        <>
          <QuestionText>{t('relationship.employee')}</QuestionText>
          <CheckboxContainer>
            <StyledSelect
              id="employee"
              name="employee"
              placeholder={t('optionPlaceholder') || ''}
              options={[
                { label: t('yes'), value: 'Yes' },
                { label: t('no'), value: 'No' }
              ]}
              errorIcon={Icon('error')}
              error={formValues.errors.employee}
              defaultOption={formValues.employee}
              onChange={(value: OptionType) => {
                const unsureRelationship =
                  formValues.relationshipSynchrony?.value === 'No' &&
                  value.value === 'No'
                handleState({
                  ...formValues,
                  employee: value,
                  rightToOptOutOfSharing:
                    value.value === 'Yes'
                      ? false
                      : formValues.rightToOptOutOfSharing,
                  rightToTransparencyForProfiling:
                    value.value === 'Yes'
                      ? false
                      : formValues.rightToOptOutOfSharing,
                  unsureRelationship
                })
              }}
              rightIconOpen={Icon('up')}
              rightIconClosed={Icon('down')}
              data-testid="employee"
            />
            {formValues.employee.value === 'Yes' && (
              <Message>{t('relationship.employeeMessage')}</Message>
            )}
          </CheckboxContainer>
        </>
      )}
      <CheckboxContainerPadding style={{ marginTop: '2rem' }}>
        {showAdditionalRelationshipQuestion(formValues.state.value) && (
          <StyledCheckbox
            id="unsureRelationship"
            isChecked={formValues.unsureRelationship}
            isDisabled={
              noRelationshipWithSynchrony && formValues.employee?.value === 'No'
            }
            error={formValues.errors.unsureRelationship}
            onChange={() => {
              const checked = !formValues.unsureRelationship
              handleState({
                ...formValues,
                unsureRelationship: checked
              })
            }}
            errorIcon={Icon('error')}
            data-testid="unsure-relationship"
          >
            {t('relationship.unsure')}
          </StyledCheckbox>
        )}
        <StyledTextarea
          id="additionalNotes"
          name="additionalNotes"
          placeholder={t('relationship.additionalNotes')}
          aria-label="additionalNotes"
          error={formValues.errors.additionalNotes}
          value={formValues.additionalNotes}
          onChange={e => {
            const unsure =
              formValues.relationshipSynchrony.value === 'Yes' &&
              !formValues.creditCardChecked &&
              !formValues.savingAccountChecked &&
              !formValues.installmentAccountChecked &&
              !formValues.businessCommercialAccountChecked &&
              !!e.target.value
            const newState: FormState = {
              ...formValues,
              additionalNotes: (e.target as HTMLTextAreaElement).value
            }
            if (!formValues.unsureRelationship && unsure)
              newState.unsureRelationship = unsure
            handleState(newState)
          }}
          maxLength={500}
          errorIcon={Icon('error')}
          data-testid="add-info"
        />
      </CheckboxContainerPadding>
    </ParentRelationshipGrid>
  )
}

export default Relationship
